import React, { useState } from 'react';
import { X, Truck, User, Calendar, DollarSign, MapPin, Zap, RefreshCw, Save, Clock, Navigation, CheckCircle2 } from 'lucide-react';

function formatSecToMin(sec = 0) {
  const m = Math.round((sec || 0) / 60);
  const h = Math.floor(m / 60);
  const remM = m % 60;
  if (h === 0) return `${remM} min`;
  return `${h}h ${remM}m`;
}

export default function JobCardModal({ job, isOpen, onClose, onSaveJob, drivers = [], trucks = [], hubs = [], stops = [] }) {
  if (!isOpen || !job) return null;

  const [formData, setFormData] = useState({
    id: job.id,
    display_name: job.display_name || '',
    round_id: job.round_id || 1,
    driver_id: job.driver_id || (drivers[0]?.id || 1),
    truck_id: job.truck_id || (trucks[0]?.id || 1),
    start_hub_id: job.start_hub_id || (hubs[0]?.id || 1),
    end_hub_id: job.end_hub_id || (hubs[0]?.id || 1),
    status: job.status || 'planned',
    estimated_departure: job.estimated_departure || '07:30',
    real_departure: job.real_departure || '',
    estimated_cost: job.estimated_cost || 0.00,
    real_cost: job.real_cost || 0.00,
    estimated_length_km: job.estimated_length_km || 0.00,
    real_length_km: job.real_length_km || 0.00,
    estimated_duration_sec: job.estimated_duration_sec || 0,
    real_duration_sec: job.real_duration_sec || 0,
    real_start: job.real_start || '',
    real_end: job.real_end || '',
    job_comments: job.job_comments || '',
    is_optimized: job.is_optimized || false
  });

  const jobStops = stops.filter(s => s.job_id === job.id);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSaveJob(job.id, formData);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="overlay-card" style={{ width: '820px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden', borderRadius: '12px' }}>
        
        {/* Header */}
        <div style={{ background: '#1e293b', padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h2 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#f8fafc' }}>
                Mission #{formData.id} - {formData.display_name || 'Nouvelle Tournée'}
              </h2>
              <span className={`status-pill ${formData.status === 'completed' ? 'completed' : 'ready'}`}>
                {formData.status.toUpperCase()}
              </span>
              {formData.is_optimized && (
                <span style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', border: '1px solid #166534', padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <CheckCircle2 size={13} /> OPTIMALE (VROOM)
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '2px' }}>
              {jobStops.length} Livraisons rattachées | Coût Estimé: <b style={{ color: '#38bdf8' }}>{parseFloat(formData.estimated_cost || 0).toFixed(2)} CHF</b>
            </div>
          </div>

          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {/* Body Form */}
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', color: '#f8fafc' }}>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Nom de la Mission *</label>
              <input type="text" className="search-input" required value={formData.display_name} onChange={e => setFormData({ ...formData, display_name: e.target.value })} />
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Chauffeur Attribué</label>
              <select className="search-input" value={formData.driver_id} onChange={e => setFormData({ ...formData, driver_id: parseInt(e.target.value) })}>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.first_name} {d.last_name} ({d.driving_license || 'Permis B'})</option>)}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Véhicule Attribué</label>
              <select className="search-input" value={formData.truck_id} onChange={e => setFormData({ ...formData, truck_id: parseInt(e.target.value) })}>
                {trucks.map(t => <option key={t.id} value={t.id}>{t.name} ({t.plate}) - {t.required_license || 'B'}</option>)}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Hub de Départ</label>
              <select className="search-input" value={formData.start_hub_id} onChange={e => setFormData({ ...formData, start_hub_id: parseInt(e.target.value) })}>
                {hubs.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Hub de Retour</label>
              <select className="search-input" value={formData.end_hub_id} onChange={e => setFormData({ ...formData, end_hub_id: parseInt(e.target.value) })}>
                {hubs.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Statut de la Mission</label>
              <select className="search-input" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                <option value="planned">Planifiée (planned)</option>
                <option value="in_progress">En Cours (in_progress)</option>
                <option value="completed">Terminée (completed)</option>
                <option value="canceled">Annulée (canceled)</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Heure de Départ Estimée</label>
              <input type="time" className="search-input" value={formData.estimated_departure ? formData.estimated_departure.slice(0,5) : '07:30'} onChange={e => setFormData({ ...formData, estimated_departure: e.target.value })} />
            </div>
          </div>

          {/* ALL ESTIMATED VS REAL VALUES FROM DATABASE */}
          <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: '800', color: '#38bdf8', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              📊 Valeurs en Base de Données (Estimé vs Réel)
            </h4>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
              {/* Coût */}
              <div style={{ background: '#1e293b', padding: '8px', borderRadius: '6px', border: '1px solid #334155' }}>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block' }}>Coût Estimé</span>
                <b style={{ color: '#38bdf8', fontSize: '0.9rem' }}>{parseFloat(formData.estimated_cost || 0).toFixed(2)} CHF</b>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px' }}>
                  Réel: <b style={{ color: '#22c55e' }}>{parseFloat(formData.real_cost || 0).toFixed(2)} CHF</b>
                </div>
              </div>

              {/* Distance */}
              <div style={{ background: '#1e293b', padding: '8px', borderRadius: '6px', border: '1px solid #334155' }}>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block' }}>Distance Estimée</span>
                <b style={{ color: '#38bdf8', fontSize: '0.9rem' }}>{parseFloat(formData.estimated_length_km || 0).toFixed(2)} km</b>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px' }}>
                  Réelle: <b style={{ color: '#22c55e' }}>{parseFloat(formData.real_length_km || 0).toFixed(2)} km</b>
                </div>
              </div>

              {/* Durée */}
              <div style={{ background: '#1e293b', padding: '8px', borderRadius: '6px', border: '1px solid #334155' }}>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block' }}>Durée Estimée</span>
                <b style={{ color: '#38bdf8', fontSize: '0.9rem' }}>{formatSecToMin(formData.estimated_duration_sec)}</b>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px' }}>
                  Réelle: <b style={{ color: '#22c55e' }}>{formatSecToMin(formData.real_duration_sec)}</b>
                </div>
              </div>

              {/* Départ Réel */}
              <div style={{ background: '#1e293b', padding: '8px', borderRadius: '6px', border: '1px solid #334155' }}>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block' }}>Départ Réel</span>
                <b style={{ color: '#22c55e', fontSize: '0.85rem' }}>{formData.real_departure ? new Date(formData.real_departure).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }) : 'Non démarré'}</b>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px' }}>
                  Fin: <b>{formData.real_end ? new Date(formData.real_end).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }) : 'En cours'}</b>
                </div>
              </div>
            </div>

            {/* Remarques & Commentaires */}
            <div>
              <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Commentaires & Remarques (Base de Données):</label>
              <textarea
                className="search-input"
                style={{ minHeight: '50px' }}
                value={formData.job_comments}
                onChange={e => setFormData({ ...formData, job_comments: e.target.value })}
                placeholder="Notes de mission..."
              />
            </div>
          </div>

          {/* Stops List inside Mission */}
          <div style={{ borderTop: '1px solid #334155', paddingTop: '12px' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#f8fafc', marginBottom: '8px' }}>
              Livraisons de la Tournée ({jobStops.length})
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '160px', overflowY: 'auto' }}>
              {jobStops.map((s, idx) => (
                <div key={s.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ background: '#38bdf8', color: '#0f172a', width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.7rem' }}>
                      {idx + 1}
                    </span>
                    <strong>{s.client_name}</strong>
                    <span style={{ color: '#94a3b8' }}>({s.city})</span>
                    <span style={{ fontSize: '0.72rem', color: '#38bdf8' }}>[Est. #{s.estimated_index || (idx + 1)}]</span>
                  </div>
                  <div>
                    <b>{s.weight_kg} kg</b> | {s.volume_m3} m³ | <b style={{ color: '#22c55e' }}>{s.revenue || 0} CHF</b>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ borderTop: '1px solid #334155', paddingTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" className="btn-action" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn-action" style={{ background: '#38bdf8', color: '#0f172a', border: 'none', fontWeight: '700', padding: '8px 18px' }}>
              <Save size={16} /> Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
